import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, XCircle } from "@phosphor-icons/react";
import { GoogleLogin } from '@react-oauth/google';

export default function Register() {
  const { register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [available, setAvailable] = useState(null); // null|true|false
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const u = username.toLowerCase().trim();
    if (!u || u.length < 3) { setAvailable(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-username`, { params: { username: u } });
        setAvailable(!!data.available);
      } catch (_) { setAvailable(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await register({ email, password, username: username.toLowerCase().trim(), name });
    setBusy(false);
    if (r.ok) nav("/dashboard");
    else setErr(r.error);
  };

  const handleGoogleSuccess = async (response) => {
    setBusy(true);
    setErr("");
    const r = await googleLogin(response.credential);
    setBusy(false);
    if (r.ok) nav("/dashboard");
    else setErr(r.error);
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-12">
      <div className="hidden lg:flex col-span-5 bg-foreground text-background p-12 flex-col justify-between">
        <div className="eyebrow opacity-80">Inkspot — Create</div>
        <div>
          <h1 className="font-serif text-6xl font-black leading-[0.95]">
            Pick a<br /><span className="italic font-medium">URL.</span><br />Start a column.
          </h1>
          <p className="mt-6 max-w-sm text-sm opacity-80 leading-relaxed">
            Your username becomes your blog's address. Choose carefully — readers will remember it.
          </p>
        </div>
        <div className="text-xs opacity-70">© {new Date().getFullYear()} Inkspot</div>
      </div>
      <div className="col-span-12 lg:col-span-7 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="eyebrow text-muted-foreground mb-3">Create account</div>
          <h2 className="font-serif text-4xl font-black tracking-tight">Get your byline.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Already a writer? <Link to="/login" className="link-underline text-foreground">Sign in</Link>.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label className="eyebrow text-muted-foreground">Display name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none mt-2 h-12" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Username · your site URL</Label>
              <div className="mt-2 flex items-stretch border border-border bg-card">
                <span className="px-3 text-sm text-muted-foreground border-r border-border grid place-items-center font-mono">/</span>
                <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} required minLength={3} maxLength={30}
                  className="rounded-none border-0 h-12 font-mono" placeholder="yourname" data-testid="register-username-input" />
                <div className="px-3 grid place-items-center">
                  {available === true && <CheckCircle size={18} weight="duotone" className="text-emerald-600" />}
                  {available === false && <XCircle size={18} weight="duotone" className="text-destructive" />}
                </div>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">3–30 chars · letters, numbers, hyphens.</div>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none mt-2 h-12" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Password</Label>
              <Input type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-none mt-2 h-12" data-testid="register-password-input" />
            </div>
            {err ? <div className="text-sm text-destructive" data-testid="register-error">{err}</div> : null}
            <Button type="submit" disabled={busy || available === false} className="rounded-none h-12 w-full bg-foreground text-background hover:bg-foreground/90" data-testid="register-submit-button">
              {busy ? "Creating..." : (<>Create my journal <ArrowRight size={16} weight="bold" className="ml-2" /></>)}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground eyebrow">Or continue with</span></div>
            </div>

            <div className="w-full">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setErr("Google Sign In failed")}
                useOneTap
                theme="outline"
                shape="square"
                width="100%"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
